import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Form, Alert } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import { isSuperAdmin, isOperationsAdmin } from '../../utils/roleBasedAccess';
import '../../pages/ManageTournaments/ManageTournaments.css'; // For general styling

// Dummy data for react-select options
const DUMMY_TOURNAMENTS = [
  { value: 't1', label: 'SFL Football League Mumbai Edition' },
  { value: 't2', label: 'SFL Football League Pune Edition' },
  { value: 't3', label: 'SFL Tennis Championship Bengaluru' },
];

const DUMMY_AGE_CATEGORIES: { [key: string]: { value: string; label: string }[] } = {
  't1': [
    { value: 'u8', label: 'U8(Co-Ed)' },
    { value: 'u10', label: 'U10 Boys' },
  ],
  't2': [
    { value: 'u9', label: 'U9 Girls' },
    { value: 'u11', label: 'U11 Co-Ed' },
  ],
  't3': [
    { value: 'u12', label: 'U12 Boys' },
    { value: 'u14', label: 'U14 Girls' },
  ],
};

const TOURNAMENT_TYPES = [
  { value: 'group_knockout', label: 'Group + Knockout' },
  { value: 'direct_knockout', label: 'Direct Knockout' },
];

const AddFixturePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);

  const [formData, setFormData] = useState({
    tournament: null as { value: string; label: string } | null,
    ageCategory: null as { value: string; label: string } | null,
    tournamentType: null as { value: string; label: string } | null,
  });
  const [availableAgeCategories, setAvailableAgeCategories] = useState<{ value: string; label: string }[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user has access
  const userData = { ...user, subRole: role, permissions: permissions };
  const isSuperAdminUser = isSuperAdmin(userData);
  const isOperationsAdminUser = isOperationsAdmin(userData);

  useEffect(() => {
    if (formData.tournament) {
      setAvailableAgeCategories(DUMMY_AGE_CATEGORIES[formData.tournament.value] || []);
    } else {
      setAvailableAgeCategories([]);
    }
    setFormData(prev => ({ ...prev, ageCategory: null })); // Reset age category when tournament changes
  }, [formData.tournament]);

  const handleTournamentChange = (selectedOption: any) => {
    setFormData(prev => ({ ...prev, tournament: selectedOption }));
  };

  const handleAgeCategoryChange = (selectedOption: any) => {
    setFormData(prev => ({ ...prev, ageCategory: selectedOption }));
  };

  const handleTournamentTypeChange = (selectedOption: any) => {
    setFormData(prev => ({ ...prev, tournamentType: selectedOption }));
  };

  const isFormValid = formData.tournament && formData.ageCategory && formData.tournamentType;

  const handleSubmit = async () => {
    if (!isFormValid) {
      setSubmitError('Please fill all required fields.');
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('Submitting Fixture:', formData);
      // In a real scenario, you'd call an API here
      // const response = await addFixture(formData);
      navigate('/fixtures', { state: { successMessage: 'Fixture added successfully!' } });
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to add fixture. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/fixtures');
  };

  if (!isSuperAdminUser && !isOperationsAdminUser) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <h3>Access Denied</h3>
            <p>You don't have permission to access this page.</p>
            <Button variant="primary" onClick={() => navigate('/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Dashboard', onClick: () => navigate('/dashboard') },
    { label: 'Manage Tournaments', onClick: () => navigate('/fixtures') },
    { label: 'Add Fixture', active: true }
  ];

  return (
    <div className="page-content">
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Header className="bg-white border-bottom">
                <div className="d-flex align-items-center" style={{ gap: '16px' }}>
                  <button
                    className="back-button"
                    onClick={() => navigate('/fixtures')}
                  >
                    <i className="ri-arrow-left-line"></i>
                  </button>
                  <h4 className="mb-0">Add New Fixtures</h4>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                {submitError && <Alert variant="danger">{submitError}</Alert>}

                <Row>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">Tournament</label>
                      <Select
                        value={formData.tournament}
                        onChange={handleTournamentChange}
                        options={DUMMY_TOURNAMENTS}
                        placeholder="Select tournament name"
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">Age category</label>
                      <Select
                        value={formData.ageCategory}
                        onChange={handleAgeCategoryChange}
                        options={availableAgeCategories}
                        placeholder="Select age category"
                        isClearable
                        isDisabled={!formData.tournament}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <div className="mb-4">
                      <label className="form-label fw-semibold">Tournament Type</label>
                      <Select
                        value={formData.tournamentType}
                        onChange={handleTournamentTypeChange}
                        options={TOURNAMENT_TYPES}
                        placeholder="Select tournament type"
                        isClearable
                        isDisabled={!formData.ageCategory}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    {/* Empty column for spacing */}
                  </Col>
                </Row>

                <div className="d-flex gap-3 justify-content-end">
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={!isFormValid || isSubmitting}
                  >
                    {isSubmitting ? 'Adding...' : 'Add →'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AddFixturePage;
